/* SettingsPage — account settings: delete account with identity confirmation,
   explicit deletion warning, and a post-delete confirmation screen. */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const DELETED_ITEMS = [
  'Your account and sign-in access',
  'Your Clarity Card (including the public share link)',
  'Quiz results and test attempts',
  'Story answers and career previews history',
  'Badges, shortlisted careers and saved life skills',
  'AI mentor conversations',
];

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (!deleted && user === false) navigate('/signin', { replace: true, state: { next: '/settings' } });
  }, [user, deleted, navigate]);

  const deleteAccount = async () => {
    if (!password) { setError('Enter your password to confirm.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/api/auth/delete-account`, { password });
      setDeleted(true);
      setUser(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete account. Check your password.');
    }
    setBusy(false);
  };

  if (deleted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-28 pb-20 px-4">
          <div className="max-w-md mx-auto bg-white rounded-3xl border border-gray-200 p-8 text-center" data-testid="delete-account-done">
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
            <h1 className="text-2xl font-black text-gray-900 mb-2">Your account has been deleted</h1>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              You have been signed out. Your personal data is being erased per our{' '}
              <Link to="/privacy" className="text-teal-700 font-semibold underline underline-offset-2">Privacy Policy</Link>{' '}
              (payment, security and legal logs are retained only as stated there). Thank you for exploring with us.
            </p>
            <Link to="/" className="inline-flex items-center justify-center bg-gray-900 text-white font-bold text-sm px-8 rounded-full hover:bg-gray-800 transition-colors" style={{ minHeight: 44 }} data-testid="delete-done-home">
              Back to homepage
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="pt-28 pb-20 px-4">
        <div className="max-w-xl mx-auto" data-testid="settings-page">
          <h1 className="text-3xl font-black text-gray-900 mb-1">Account settings</h1>
          <p className="text-sm text-gray-500 mb-8">{user?.email}</p>

          <div className="bg-white rounded-2xl border-2 border-rose-200 p-6" data-testid="delete-account-section">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 size={18} className="text-rose-600" />
              <h2 className="text-lg font-extrabold text-gray-900">Delete my account</h2>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              This is permanent. Deleting your account will erase, per our{' '}
              <Link to="/privacy" className="text-teal-700 font-semibold underline underline-offset-2" data-testid="settings-privacy-link">Privacy Policy</Link>:
            </p>
            <ul className="space-y-1.5 mb-4" data-testid="delete-warning-list">
              {DELETED_ITEMS.map((it) => (
                <li key={it} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-rose-400 shrink-0">•</span>{it}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mb-4">
              Payment, security and legal records are retained only where the law requires, as stated in the Privacy Policy. See also our{' '}
              <Link to="/terms" className="text-teal-700 font-semibold underline underline-offset-2" data-testid="settings-terms-link">Terms of Service</Link>.
            </p>

            {!confirming ? (
              <button type="button" onClick={() => setConfirming(true)}
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm px-6 rounded-full transition-colors"
                style={{ minHeight: 44 }}
                data-testid="delete-account-start">
                Delete my account
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-rose-700 flex items-center gap-1.5">
                  <AlertTriangle size={14} /> Confirm your identity — enter your password:
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-rose-400 focus:outline-none"
                  data-testid="delete-account-password"
                />
                {error && <p className="text-sm text-rose-600" data-testid="delete-account-error">{error}</p>}
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={deleteAccount} disabled={busy}
                    className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-sm px-6 rounded-full transition-colors inline-flex items-center gap-2"
                    style={{ minHeight: 44 }}
                    data-testid="delete-account-confirm">
                    {busy && <Loader2 size={14} className="animate-spin" />} Permanently delete
                  </button>
                  <button type="button" onClick={() => { setConfirming(false); setPassword(''); setError(''); }}
                    className="text-sm font-bold text-gray-600 px-4" style={{ minHeight: 44 }}
                    data-testid="delete-account-cancel">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
